import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  TaskNameItem,
  ApprovalStatusItem,
  InboxTaskListItem,
  InboxTaskListResponse,
  ApiResponse,
} from '@/api/types'

/**
 * Inbox Tasks (Task Inbox list) service
 * GET /api/InboxTasks/TaskNames, ApprovalStatuses, GetAll
 */
export class InboxTasksService {
  async getTaskNames(): Promise<TaskNameItem[]> {
    const url = endpoints.inboxTasks.taskNames
    const response = await apiClient.get<ApiResponse<TaskNameItem[]> | TaskNameItem[] | any>(url)
    const payload = response.data
    if (payload && typeof payload === 'object') {
      if ('success' in payload && Array.isArray(payload.data)) {
        return (payload.data as any[]).map(normalizeTaskName)
      }
      if ('data' in payload && Array.isArray(payload.data)) {
        return (payload.data as any[]).map(normalizeTaskName)
      }
      if (Array.isArray(payload)) {
        return (payload as any[]).map(normalizeTaskName)
      }
    }
    return []
  }

  async getApprovalStatuses(): Promise<ApprovalStatusItem[]> {
    const url = endpoints.inboxTasks.approvalStatuses
    const response = await apiClient.get<ApiResponse<ApprovalStatusItem[]> | ApprovalStatusItem[] | any>(url)
    const payload = response.data
    if (payload && typeof payload === 'object') {
      if ('success' in payload && Array.isArray(payload.data)) {
        return (payload.data as any[]).map(normalizeApprovalStatus)
      }
      if ('data' in payload && Array.isArray(payload.data)) {
        return (payload.data as any[]).map(normalizeApprovalStatus)
      }
      if (Array.isArray(payload)) {
        return (payload as any[]).map(normalizeApprovalStatus)
      }
    }
    return []
  }

  async getAll(
    taskId: number,
    statusId: number,
    branchId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<InboxTaskListResponse> {
    const url = endpoints.inboxTasks.getAll(taskId, statusId, branchId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<InboxTaskListResponse> | InboxTaskListResponse | any>(url)
    const payload = response.data
    if (payload && typeof payload === 'object') {
      if ('success' in payload && payload.data) {
        const d = payload.data
        return {
          totalRecords: d.totalRecords ?? d.data?.length ?? 0,
          pageNumber: d.pageNumber ?? pageNumber,
          pageSize: d.pageSize ?? pageSize,
          data: Array.isArray(d.data) ? (d.data as any[]).map(normalizeInboxTaskItem) : [],
        }
      }
      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: (payload.data as any[]).map(normalizeInboxTaskItem),
        }
      }
      if ('totalRecords' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: (payload.data as any[]).map(normalizeInboxTaskItem),
        }
      }
    }
    return { totalRecords: 0, pageNumber, pageSize, data: [] }
  }

  async getById(id: number): Promise<InboxTaskListItem | null> {
    const url = endpoints.inboxTasks.getById(id)
    const response = await apiClient.get<ApiResponse<InboxTaskListItem> | InboxTaskListItem | any>(url)
    const payload = response.data
    if (payload && typeof payload === 'object') {
      const data = ('data' in payload && payload.data) ? payload.data : ('success' in payload && payload.data) ? payload.data : payload
      if (data && typeof data === 'object') {
        return normalizeInboxTaskItem(data)
      }
    }
    return null
  }

  /**
   * Get notifications for the current user / branch
   * GET /api/InboxTasks/GetNotifications?branchId={branchId}
   */
  async getNotifications(branchId: number): Promise<InboxTaskListItem[]> {
    const url = endpoints.inboxTasks.getNotifications(branchId)
    const response = await apiClient.get<ApiResponse<{ totalRecords: number; data: any[] }> | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      const dataArray =
        Array.isArray(payload) ? payload :
        Array.isArray((payload as any).data) ? (payload as any).data :
        []

      if (Array.isArray(dataArray)) {
        return (dataArray as any[]).map(normalizeInboxTaskItem)
      }
    }

    return []
  }
}

function normalizeTaskName(item: any): TaskNameItem {
  return {
    id: item.id ?? item.Id ?? 0,
    lookupCodeId: item.lookupCodeId ?? item.LookupCodeId,
    lookupNameEn: item.lookupNameEn ?? item.LookupNameEn ?? '',
    lookupNameAr: item.lookupNameAr ?? item.LookupNameAr ?? '',
    isActive: item.isActive ?? item.IsActive,
    parentCode: item.parentCode ?? item.ParentCode,
    createdById: item.createdById ?? item.CreatedById,
    createdDate: item.createdDate ?? item.CreatedDate,
    updatedById: item.updatedById ?? item.UpdatedById,
    updatedDate: item.updatedDate ?? item.UpdatedDate,
    companyId: item.companyId ?? item.CompanyId,
    branchId: item.branchId ?? item.BranchId,
  }
}

function normalizeApprovalStatus(item: any): ApprovalStatusItem {
  return {
    id: item.id ?? item.Id ?? 0,
    lookupCodeId: item.lookupCodeId ?? item.LookupCodeId,
    lookupNameEn: item.lookupNameEn ?? item.LookupNameEn ?? '',
    lookupNameAr: item.lookupNameAr ?? item.LookupNameAr ?? '',
    isActive: item.isActive ?? item.IsActive,
    parentCode: item.parentCode ?? item.ParentCode,
    createdById: item.createdById ?? item.CreatedById,
    createdDate: item.createdDate ?? item.CreatedDate,
    updatedById: item.updatedById ?? item.UpdatedById,
    updatedDate: item.updatedDate ?? item.UpdatedDate,
  }
}

function normalizeInboxTaskItem(item: any): InboxTaskListItem {
  return {
    id: item.id ?? item.Id ?? 0,
    approvalRouteId: item.approvalRouteId ?? item.ApprovalRouteId,
    seqNo: item.seqNo ?? item.SeqNo ?? 0,
    approvalTaskId: item.approvalTaskId ?? item.ApprovalTaskId ?? 0,
    approvalUserId: item.approvalUserId ?? item.ApprovalUserId,
    requestedBy: item.requestedBy ?? item.RequestedBy,
    recordId: item.recordId ?? item.RecordId,
    recordName: item.recordName ?? item.RecordName,
    approvalStatusId: item.approvalStatusId ?? item.ApprovalStatusId ?? 0,
    recordNotes: item.recordNotes ?? item.RecordNotes,
    approvalNote: item.approvalNote ?? item.ApprovalNote,
    controllerName: item.controllerName ?? item.ControllerName,
    actionName: item.actionName ?? item.ActionName,
    createdById: item.createdById ?? item.CreatedById,
    createdDate: item.createdDate ?? item.CreatedDate,
    updatedById: item.updatedById ?? item.UpdatedById,
    updatedDate: item.updatedDate ?? item.UpdatedDate,
    branchId: item.branchId ?? item.BranchId,
  }
}

export const inboxTasksService = new InboxTasksService()
